import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Plus, Crown, QrCode, ChevronRight, Search, Loader2, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtAUD = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n || 0);

export const MemberStatusBadge = ({ status }) => {
  const map = {
    active: 'bg-emerald-100 text-emerald-700',
    pending: 'bg-amber-100 text-amber-700',
    suspended: 'bg-orange-100 text-orange-700',
    closed: 'bg-slate-200 text-slate-700',
  };
  return <Badge className={`${map[status] || ''} hover:${map[status] || ''}`}>{status}</Badge>;
};

export default function MembershipAccountsTab() {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [approving, setApproving] = useState(null);
  const [downloadingQr, setDownloadingQr] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [mRes, dRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/memberships${search ? `?search=${encodeURIComponent(search)}` : ''}`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/memberships/dashboard`, { credentials: 'include' }),
      ]);
      if (mRes.ok) setMembers(await mRes.json());
      if (dRes.ok) setDashboard(await dRes.json());
    } catch { toast.error('Failed to load member accounts'); }
    finally { setLoading(false); }
  };
   
  useEffect(() => { refresh(); }, [search]);

  const downloadQr = async () => {
    setDownloadingQr(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/memberships/signup-qr.pdf`, { credentials: 'include' });
      if (!r.ok) throw new Error('QR download failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'member-signup-qr-a3.pdf';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('A3 signup QR poster downloaded');
    } catch (e) { toast.error(e.message); }
    finally { setDownloadingQr(false); }
  };

  return (
    <div data-testid="membership-accounts-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-slate-500 text-sm">
          Business members paying a monthly subscription — credit accumulates towards their invoices
        </p>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowCreate(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="create-member-btn">
            <Plus className="h-4 w-4 mr-2" /> Create Member Account
          </Button>
          <Button variant="outline" onClick={downloadQr} disabled={downloadingQr} data-testid="download-signup-qr-btn"
            title="Download the A3 signup QR poster for the promo night banner">
            {downloadingQr ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <QrCode className="h-4 w-4 mr-2" />}
            Member Sign-Up QR (PDF)
          </Button>
        </div>
      </div>

      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          <Kpi label="Active members" value={dashboard.active_members} tone="emerald" testid="kpi-active-members" />
          <Kpi label="Pending signups" value={dashboard.pending_signups} tone={dashboard.pending_signups ? 'amber' : 'slate'} testid="kpi-pending-signups" />
          <Kpi label="Accumulated" value={fmtAUD(dashboard.total_accumulated)} tone="blue" testid="kpi-accumulated" />
          <Kpi label="Used" value={fmtAUD(dashboard.total_used)} testid="kpi-used" />
          <Kpi label="Balance" value={fmtAUD(dashboard.total_balance)} tone={dashboard.total_balance < 0 ? 'rose' : 'emerald'} testid="kpi-balance" />
          <Kpi label="Receipts to review" value={dashboard.receipts_awaiting_approval} tone={dashboard.receipts_awaiting_approval ? 'amber' : 'slate'} testid="kpi-receipts-review" />
        </div>
      )}

      <Card className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <Search className="h-4 w-4 text-slate-400" />
          <Input placeholder="Search company / name / email / mobile…" value={search}
            onChange={(e) => setSearch(e.target.value)} className="max-w-md h-9" data-testid="member-search" />
        </div>

        {loading ? (
          <div className="py-12 text-center text-slate-500"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
        ) : members.length === 0 ? (
          <div className="py-16 text-center text-slate-500">
            <Crown className="h-12 w-12 mx-auto mb-3 opacity-40" />
            No member accounts yet. Create one, or print the signup QR for your promo night.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Member</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-right">Monthly</TableHead>
                <TableHead className="text-right">Accumulated</TableHead>
                <TableHead className="text-right">Used</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead className="text-center">This month</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.member_id} className="cursor-pointer hover:bg-slate-50"
                  onClick={() => m.status !== 'pending' && navigate(`/memberships/${m.member_id}`)}
                  data-testid="member-row">
                  <TableCell>
                    <p className="font-medium text-slate-900">{m.company_name || `${m.first_name} ${m.last_name || ''}`}</p>
                    <p className="text-xs text-slate-500">{m.first_name} {m.last_name || ''}{m.abn ? ` · ABN ${m.abn}` : ''}</p>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm text-slate-700">{m.mobile || '—'}</p>
                    <p className="text-xs text-slate-500">{m.email}</p>
                  </TableCell>
                  <TableCell className="text-center"><MemberStatusBadge status={m.status} /></TableCell>
                  <TableCell className="text-right">{m.monthly_amount ? fmtAUD(m.monthly_amount) : '—'}</TableCell>
                  <TableCell className="text-right text-emerald-700 font-medium">{fmtAUD(m.accumulated)}</TableCell>
                  <TableCell className="text-right text-slate-700">{fmtAUD(m.used)}</TableCell>
                  <TableCell className={`text-right font-semibold ${m.balance < 0 ? 'text-rose-600' : 'text-slate-900'}`}>{fmtAUD(m.balance)}</TableCell>
                  <TableCell className="text-center">
                    {m.status === 'pending' ? '—' : (
                      m.current_period_status === 'paid'
                        ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="h-3 w-3 mr-1" />Paid</Badge>
                        : m.current_period_status === 'receipt_uploaded'
                          ? <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Receipt in</Badge>
                          : m.current_period_status === 'unpaid'
                            ? <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Unpaid</Badge>
                            : <span className="text-slate-400 text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {m.status === 'pending' ? (
                      <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white h-8"
                        onClick={() => setApproving(m)} data-testid={`approve-member-btn-${m.member_id}`}>
                        Approve
                      </Button>
                    ) : (
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <CreateMemberDialog open={showCreate} onClose={() => setShowCreate(false)} onDone={refresh} />
      <ApproveMemberDialog member={approving} onClose={() => setApproving(null)} onDone={refresh} />
    </div>
  );
}

function Kpi({ label, value, tone = 'slate', testid }) {
  const tones = {
    slate: 'text-slate-900', emerald: 'text-emerald-600',
    blue: 'text-blue-600', amber: 'text-amber-600', rose: 'text-rose-600',
  };
  return (
    <Card className="p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-xl md:text-2xl font-semibold mt-1 ${tones[tone]}`}>{value ?? '—'}</p>
    </Card>
  );
}

const EMPTY_FORM = {
  company_name: '', first_name: '', last_name: '', mobile: '', email: '',
  license_number: '', abn: '', address: '', monthly_amount: '', portal_password: '',
};

function CreateMemberDialog({ open, onClose, onDone }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const close = () => { setForm(EMPTY_FORM); onClose(); };

  const submit = async () => {
    if (!form.first_name.trim()) return toast.error('First name required');
    if (!form.mobile.trim()) return toast.error('Mobile required');
    if (!form.email.trim()) return toast.error('Email required');
    if (!form.monthly_amount || parseFloat(form.monthly_amount) <= 0) return toast.error('Monthly subscription amount required');
    if (form.portal_password && form.portal_password.length < 6) return toast.error('Portal password must be at least 6 characters');
    setSubmitting(true);
    try {
      const payload = {
        company_name: form.company_name.trim() || null,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim() || null,
        mobile: form.mobile.trim(),
        email: form.email.trim(),
        license_number: form.license_number.trim() || null,
        abn: form.abn.trim() || null,
        address: form.address.trim() || null,
        monthly_amount: parseFloat(form.monthly_amount),
        portal_password: form.portal_password || null,
      };
      const r = await fetch(`${BACKEND_URL}/api/memberships`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed to create member');
      toast.success('Member account created — customer added to Customers too');
      onDone(); close();
    } catch (e) { toast.error(typeof e.message === 'string' ? e.message : 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-member-dialog">
        <DialogHeader>
          <DialogTitle>New Member Account</DialogTitle>
          <DialogDescription>Creates the member account and a matching customer record.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Company name</Label>
            <Input value={form.company_name} onChange={set('company_name')} placeholder="ACME Logistics Pty Ltd" data-testid="member-company-input" />
          </div>
          <div>
            <Label>First name *</Label>
            <Input value={form.first_name} onChange={set('first_name')} data-testid="member-firstname-input" />
          </div>
          <div>
            <Label>Last name</Label>
            <Input value={form.last_name} onChange={set('last_name')} data-testid="member-lastname-input" />
          </div>
          <div>
            <Label>Mobile *</Label>
            <Input value={form.mobile} onChange={set('mobile')} placeholder="04xx xxx xxx" data-testid="member-mobile-input" />
          </div>
          <div>
            <Label>Email *</Label>
            <Input type="email" value={form.email} onChange={set('email')} data-testid="member-email-input" />
          </div>
          <div>
            <Label>Licence number</Label>
            <Input value={form.license_number} onChange={set('license_number')} data-testid="member-license-input" />
          </div>
          <div>
            <Label>ABN</Label>
            <Input value={form.abn} onChange={set('abn')} data-testid="member-abn-input" />
          </div>
          <div className="col-span-2">
            <Label>Address</Label>
            <Input value={form.address} onChange={set('address')} data-testid="member-address-input" />
          </div>
          <div>
            <Label>Monthly subscription (AUD) *</Label>
            <Input type="number" min="1" value={form.monthly_amount} onChange={set('monthly_amount')} placeholder="99.00" data-testid="member-monthly-input" />
          </div>
          <div>
            <Label>Portal password (optional)</Label>
            <Input type="text" value={form.portal_password} onChange={set('portal_password')} placeholder="Min 6 characters" data-testid="member-password-input" />
            <p className="text-[11px] text-slate-500 mt-1">Member logs in at /member-portal to upload receipts & view invoices. You can also set this later.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-slate-900 hover:bg-slate-800" data-testid="submit-member-btn">
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create member
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApproveMemberDialog({ member, onClose, onDone }) {
  const [amount, setAmount] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const close = () => { setAmount(''); setPassword(''); onClose(); };

  const submit = async () => {
    if (!amount || parseFloat(amount) <= 0) return toast.error('Monthly amount required');
    if (password && password.length < 6) return toast.error('Password must be at least 6 characters');
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/memberships/${member.member_id}/approve`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ monthly_amount: parseFloat(amount), portal_password: password || null }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Approval failed');
      toast.success('Member approved & activated — customer record created');
      onDone(); close();
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  if (!member) return null;
  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md" data-testid="approve-member-dialog">
        <DialogHeader>
          <DialogTitle>Approve membership signup</DialogTitle>
          <DialogDescription>Set the monthly subscription to activate this member.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
            <p className="font-medium text-slate-900">{member.company_name || `${member.first_name} ${member.last_name || ''}`}</p>
            <p className="text-slate-600">{member.email} · {member.mobile}</p>
            {member.abn && <p className="text-slate-500 text-xs">ABN {member.abn}</p>}
          </div>
          <div>
            <Label>Monthly subscription (AUD) *</Label>
            <Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="99.00" data-testid="approve-monthly-input" />
          </div>
          <div>
            <Label>Portal password (optional)</Label>
            <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" data-testid="approve-password-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-member-submit">
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Approve & activate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
